'use client'
import * as React from 'react'
/*
  Next-intl is adding /en/ to the href of the Link component on the server side.
  https://next-intl-docs.vercel.app/docs/routing/middleware#locale-prefix-as-needed
  It then removes it on the client side, but this means that google is following the /en/ link.
  To fix this, we need to build the absolute path on the server side if the locale is the english one and href is a relative path.
  This will lead to hydration warnings, so we need to suppress them by using the suppressHydrationWarning React prop.
*/
import { useLocale } from 'next-intl'
import { useUserQuery } from 'auth/hooks'
// eslint-disable-next-line no-restricted-imports
import NextJSLink from 'next/link'
import { defaultLanguage } from 'cf-i18n/constants'
import { rootPath } from '@repo/web-utils/root-path'
// eslint-disable-next-line no-restricted-imports
import { usePathname } from '@repo/nextjs-utils/navigation'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'

function isAbsoluteUrl(url: string) {
  if (typeof url === 'string') {
    return (
      url.startsWith('http') ||
      url.startsWith('mailto:') ||
      url.startsWith('tel:') ||
      url.startsWith('sms:')
    )
  }
  return true
}

function hasQueryParams(url: string) {
  return url.includes('?')
}

function hasAnchor(url: string) {
  return url.includes('#')
}

//We can use this function to apply additional sanitization if needed
//The following changes are applied:
//  1. If the url is a relative path and does not have query params, we make sure that it ends with a slash
function sanitizeUrl(url: string) {
  if (typeof url === 'string' && !isAbsoluteUrl(url) && !hasQueryParams(url) && !hasAnchor(url)) {
    return `${url.endsWith('/') ? url : `${url}/`}`
  }

  return url
}

export const Link = ({
  ref,
  ...props
}: Omit<React.ComponentProps<typeof NextJSLink>, 'href'> & {
  href?: string
  ref?: React.RefObject<HTMLAnchorElement | null>
  component?: 'Link' | 'a'
  skipNormalizing?: boolean
}) => {
  const {
    href = '',
    prefetch,
    scroll,
    component,
    skipNormalizing = false,
    onMouseEnter,
    ...rest
  } = props

  // When `prefetch` is left undefined, defer prefetching until the user hints
  // intent by hovering the link (see Next.js "HoverPrefetchLink" pattern).
  // Passing `null` opts into Next.js' default prefetch behavior, `false` disables it.
  const [shouldPrefetch, setShouldPrefetch] = React.useState(false)
  const hoverPrefetch = shouldPrefetch ? null : false
  const resolvedPrefetch = prefetch === undefined ? hoverPrefetch : prefetch

  const isTestUserQuery = useUserQuery(undefined, {
    select(data) {
      return (
        data.me?.user?.email?.endsWith('@creativefabrica.com') &&
        data.me.user.firstName === 'test_user'
      )
    },
  })

  const locale = useLocale()
  const pathname = usePathname()

  const hrefWithLocale = isAbsoluteUrl(href) ? href : addOrReplaceLocaleInUrl(href, locale)

  // oxlint-disable-next-line no-nested-ternary
  const normalizedHref = skipNormalizing
    ? sanitizeUrl(href)
    : typeof window === 'undefined' && locale === defaultLanguage && !isAbsoluteUrl(href)
      ? sanitizeUrl(`${process.env.NODE_ENV === 'development' ? '' : rootPath}${href}`)
      : sanitizeUrl(hrefWithLocale)

  const Component = isTestUserQuery.data || component === 'Link' ? NextJSLink : 'a'

  return (
    <Component
      onClick={() => {
        if (pathname === props.href) {
          window.location.reload()
        } else {
          window.dispatchEvent(new Event('cf-popstate'))
        }
      }}
      onMouseEnter={event => {
        if (prefetch === undefined) {
          setShouldPrefetch(true)
        }
        onMouseEnter?.(event)
      }}
      href={href ? normalizedHref : ''}
      {...rest}
      ref={ref}
      {...(component === 'Link' ? { prefetch: resolvedPrefetch, scroll } : {})}
      suppressHydrationWarning
    />
  )
}

Link.displayName = 'CustomLink'
